import React, { useState, useEffect, useCallback } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminExamScheduleManage = ({ onNavigate, userInfo = {} }) => {
    const [isMobile, setIsMobile] = useState(false);

    // Data
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(false);

    // Departments (fetched)
    const [departments, setDepartments] = useState([]);

    // Batches (fetched based on department + year)
    const [batches, setBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);
    // Batches for the modal form (fetched based on form department + year)
    const [formBatches, setFormBatches] = useState([]);
    const [formBatchesLoading, setFormBatchesLoading] = useState(false);

    // Filters
    const [filterDepartment, setFilterDepartment] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterBatch, setFilterBatch] = useState('');

    // Add form modal
    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        department: '',
        year: '',
        batch: '',
        subject_code: '',
        subject_name: '',
        exam_date: '',
        exam_time: ''
    });

    // Responsive
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    // Fetch departments
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const res = await fetch(`${API_LINK}/api/admin/departments`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setDepartments(data.data || data.departments || []);
                }
            } catch (err) {
                console.error('Error fetching departments:', err);
            }
        };
        fetchDepartments();
    }, []);

    // Fetch batches for filter dropdowns when department or year changes
    useEffect(() => {
        const fetchBatches = async () => {
            if (!filterDepartment || !filterYear) {
                setBatches([]);
                return;
            }
            try {
                setBatchesLoading(true);
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const params = new URLSearchParams({ department: filterDepartment, year: filterYear });
                const res = await fetch(`${API_LINK}/api/admin/batches?${params}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setBatches(data.data || []);
                } else {
                    setBatches([]);
                }
            } catch (err) {
                console.error('Error fetching batches:', err);
                setBatches([]);
            } finally {
                setBatchesLoading(false);
            }
        };
        fetchBatches();
        setFilterBatch(''); // Reset batch when department/year changes
    }, [filterDepartment, filterYear]);

    // Fetch batches for modal form when form department or year changes
    useEffect(() => {
        const fetchFormBatches = async () => {
            if (!formData.department || !formData.year) {
                setFormBatches([]);
                return;
            }
            try {
                setFormBatchesLoading(true);
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const params = new URLSearchParams({ department: formData.department, year: formData.year });
                const res = await fetch(`${API_LINK}/api/admin/batches?${params}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setFormBatches(data.data || []);
                } else {
                    setFormBatches([]);
                }
            } catch (err) {
                console.error('Error fetching form batches:', err);
                setFormBatches([]);
            } finally {
                setFormBatchesLoading(false);
            }
        };
        fetchFormBatches();
        setFormData(prev => ({ ...prev, batch: '' })); // Reset batch when dept/year changes
    }, [formData.department, formData.year]);

    // Fetch exam schedule
    const fetchSubjects = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const params = new URLSearchParams();
            if (filterDepartment) params.append('department', filterDepartment);
            if (filterYear) params.append('year', filterYear);
            if (filterBatch) params.append('batch', filterBatch);

            const res = await fetch(`${API_LINK}/api/admin/exam-schedule?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setSubjects(data.data || []);
            } else {
                setSubjects([]);
            }
        } catch (err) {
            console.error('Error fetching exam schedule:', err);
            setSubjects([]);
        } finally {
            setLoading(false);
        }
    }, [filterDepartment, filterYear, filterBatch]);

    useEffect(() => {
        fetchSubjects();
    }, [fetchSubjects]);

    // Handle add subject
    const handleAddSubject = async (e) => {
        e.preventDefault();
        if (!formData.department || !formData.year || !formData.batch || !formData.subject_code || !formData.subject_name) {
            alert('Please fill in Department, Year, Batch, Subject Code, and Subject Name.');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const res = await fetch(`${API_LINK}/api/admin/exam-schedule`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
            const data = await res.json();
            if (data.success) {
                alert('Exam subject added successfully!');
                setShowAddModal(false);
                setFormData({ department: '', year: '', batch: '', subject_code: '', subject_name: '', exam_date: '', exam_time: '' });
                fetchSubjects();
            } else {
                alert('Failed to add: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error adding exam subject:', err);
            alert('Error adding exam subject.');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this exam subject?')) return;
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const res = await fetch(`${API_LINK}/api/admin/exam-schedule/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                alert('Deleted successfully!');
                fetchSubjects();
            } else {
                alert('Failed to delete.');
            }
        } catch (err) {
            console.error('Error deleting:', err);
            alert('Error deleting exam subject.');
        }
    };

    // Styles
    const cardStyle = {
        background: '#fff',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
    };

    const inputStyle = {
        width: '100%',
        padding: '10px 14px',
        border: '1.5px solid #e0e0e0',
        borderRadius: '8px',
        fontSize: '0.9rem',
        outline: 'none',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box'
    };

    const selectStyle = { ...inputStyle, appearance: 'auto' };

    const btnPrimary = {
        background: 'linear-gradient(135deg, #238548, #2ea35e)',
        color: '#fff',
        border: 'none',
        padding: '10px 24px',
        borderRadius: '8px',
        fontWeight: '600',
        cursor: 'pointer',
        fontSize: '0.9rem'
    };

    const btnDanger = {
        background: '#e74c3c',
        color: '#fff',
        border: 'none',
        padding: '6px 16px',
        borderRadius: '6px',
        fontWeight: '500',
        cursor: 'pointer',
        fontSize: '0.8rem'
    };

    const yearOptions = [
        { value: '2001', label: '1st Year' },
        { value: '2002', label: '2nd Year' },
        { value: '2003', label: '3rd Year' },
        { value: '2004', label: '4th Year' }
    ];

    return (
        <Layout role="admin" onNavigate={onNavigate} activePage="admin-exam-schedule" userInfo={userInfo}>
            {/* Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#fff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                    Exam Schedule Management 📝
                </h2>
                <p style={{ margin: 0, opacity: 0.9 }}>
                    Add and manage exam subjects by Department, Year, and Batch. These subjects appear on the Admit Card.
                </p>
            </div>

            {/* Filters */}
            <div style={cardStyle}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#333' }}>🔍 Filter Subjects</h3>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr) auto',
                    gap: '1rem',
                    alignItems: 'end'
                }}>
                    {/* Department */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Department</label>
                        <select value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)} style={selectStyle}>
                            <option value="">All Departments</option>
                            {departments.map((d, i) => (
                                <option key={i} value={d.value || d.department_name || d.name || d}>{d.label || d.department_name || d.name || d}</option>
                            ))}
                        </select>
                    </div>
                    {/* Year */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Year</label>
                        <select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={selectStyle}>
                            <option value="">All Years</option>
                            {yearOptions.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                        </select>
                    </div>
                    {/* Batch */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Batch</label>
                        <select value={filterBatch} onChange={e => setFilterBatch(e.target.value)} style={selectStyle} disabled={!filterDepartment || !filterYear || batchesLoading}>
                            <option value="">{batchesLoading ? 'Loading...' : (!filterDepartment || !filterYear) ? 'Select Dept & Year first' : 'All Batches'}</option>
                            {batches.map((b, i) => <option key={i} value={b}>{b}</option>)}
                        </select>
                    </div>
                    {/* Add Button */}
                    <div>
                        <button style={btnPrimary} onClick={() => {
                            setFormData(prev => ({
                                ...prev,
                                department: filterDepartment || prev.department,
                                year: filterYear || prev.year,
                                batch: filterBatch || prev.batch
                            }));
                            setShowAddModal(true);
                        }}>
                            + Add Subject
                        </button>
                    </div>
                </div>
            </div>

            {/* Subjects Table */}
            <div style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>
                        📋 Exam Subjects ({subjects.length})
                    </h3>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                        <p>Loading exam subjects...</p>
                    </div>
                ) : subjects.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📝</div>
                        <p style={{ fontSize: '1rem' }}>No exam subjects found. Use the <strong>+ Add Subject</strong> button to add subjects.</p>
                        <p style={{ fontSize: '0.85rem', color: '#aaa' }}>Apply filters and then add subjects – they will auto-fill in the form.</p>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>#</th>
                                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Department</th>
                                    <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Year</th>

                                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Batch</th>
                                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Subject Code</th>
                                    <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Subject Name</th>
                                    <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Exam Date</th>
                                    <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Exam Time</th>
                                    <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subjects.map((sub, idx) => (
                                    <tr key={sub.id} style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f8fdf9'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ padding: '10px' }}>{idx + 1}</td>
                                        <td style={{ padding: '10px', fontWeight: 500 }}>{sub.department}</td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>{sub.year}</td>

                                        <td style={{ padding: '10px' }}>{sub.batch || '—'}</td>
                                        <td style={{ padding: '10px' }}>
                                            <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '3px 10px', borderRadius: '12px', fontWeight: 600, fontSize: '0.8rem' }}>
                                                {sub.subject_code}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px', maxWidth: '200px' }}>{sub.subject_name}</td>
                                        <td style={{ padding: '10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {sub.exam_date ? new Date(sub.exam_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>{sub.exam_time || '—'}</td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>
                                            <button style={btnDanger} onClick={() => handleDelete(sub.id)}>🗑 Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }} onClick={() => setShowAddModal(false)}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '16px',
                        width: '95%',
                        maxWidth: '600px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        padding: '2rem',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ margin: 0, color: '#333' }}>📝 Add Exam Subject</h2>
                            <button onClick={() => setShowAddModal(false)} style={{
                                background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999'
                            }}>✕</button>
                        </div>

                        <form onSubmit={handleAddSubject}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                {/* Department */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Department *</label>
                                    <select
                                        value={formData.department}
                                        onChange={e => setFormData({ ...formData, department: e.target.value })}
                                        style={selectStyle}
                                        required
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map((d, i) => (
                                            <option key={i} value={d.value || d.department_name || d.name || d}>{d.label || d.department_name || d.name || d}</option>
                                        ))}
                                    </select>
                                </div>
                                {/* Year */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Year *</label>
                                    <select value={formData.year} onChange={e => setFormData({ ...formData, year: e.target.value })} style={selectStyle} required>
                                        <option value="">Select Year</option>
                                        {yearOptions.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                    </select>
                                </div>
                                {/* Batch */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Batch *</label>
                                    <select value={formData.batch} onChange={e => setFormData({ ...formData, batch: e.target.value })} style={selectStyle} required disabled={!formData.department || !formData.year || formBatchesLoading}>
                                        <option value="">{formBatchesLoading ? 'Loading batches...' : (!formData.department || !formData.year) ? 'Select Dept & Year first' : 'Select Batch'}</option>
                                        {formBatches.map((b, i) => <option key={i} value={b}>{b}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1rem' }}>
                                {/* Subject Code */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Subject Code *</label>
                                    <input type="text" placeholder="e.g., CS301" value={formData.subject_code} onChange={e => setFormData({ ...formData, subject_code: e.target.value })} style={inputStyle} required />
                                </div>
                                {/* Subject Name */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Subject Name *</label>
                                    <input type="text" placeholder="e.g., Data Structures" value={formData.subject_name} onChange={e => setFormData({ ...formData, subject_name: e.target.value })} style={inputStyle} required />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                {/* Exam Date */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Exam Date</label>
                                    <input type="date" value={formData.exam_date} onChange={e => setFormData({ ...formData, exam_date: e.target.value })} style={inputStyle} />
                                </div>
                                {/* Exam Time */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Exam Time</label>
                                    <input type="text" placeholder="e.g., 10:00 AM - 01:00 PM" value={formData.exam_time} onChange={e => setFormData({ ...formData, exam_time: e.target.value })} style={inputStyle} />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => setShowAddModal(false)} style={{
                                    background: '#f0f0f0', color: '#555', border: 'none', padding: '10px 24px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
                                }}>Cancel</button>
                                <button type="submit" disabled={submitting} style={{
                                    ...btnPrimary,
                                    opacity: submitting ? 0.7 : 1
                                }}>
                                    {submitting ? '⏳ Adding...' : '✅ Add Subject'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default AdminExamScheduleManage;
